// First-run onboarding state (L0-U10, ADR 0024 D1 / ADR 0031).
//
// The first-run wizard appears once for a tenant that still has zero accounts.
// This flag records that the user has already seen and dismissed (or completed)
// the wizard, so it does not reappear on every page load even while the tenant
// remains empty -- the Import-page safety net is the hard floor in that case.
// Stored client-side only; tolerant of storage errors (private-mode browsers
// throw on localStorage), returning the safe default rather than throwing.

const WIZARD_DISMISSED_KEY = "onboardingWizardDismissed";
// Set once the product tour has been finished or skipped (L0-U10 session 2).
// Drives whether the dashboard's "Take a tour" card auto-prompts; the tour is
// always re-launchable from Settings regardless of this flag.
const TOUR_COMPLETED_KEY = "onboardingTourCompleted";
// Set when the user dismisses the dashboard tour card ("Maybe later") without
// taking the tour, so the card does not nag on every dashboard visit.
const TOUR_CARD_DISMISSED_KEY = "onboardingTourCardDismissed";

export function isOnboardingWizardDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(WIZARD_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

export function dismissOnboardingWizard(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WIZARD_DISMISSED_KEY, "true");
  } catch {
    // Best-effort: if storage is unavailable the wizard simply re-prompts.
  }
}

// Clears the dismissal so the wizard (or a future re-launchable tour, L0-U10
// session 2) can be shown again from a help/settings entry point.
export function resetOnboardingWizard(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(WIZARD_DISMISSED_KEY);
  } catch {
    // ignore
  }
}

export function isTourCompleted(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(TOUR_COMPLETED_KEY) === "true";
  } catch {
    return false;
  }
}

export function markTourCompleted(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TOUR_COMPLETED_KEY, "true");
  } catch {
    // Best-effort: the tour simply re-offers itself if storage is unavailable.
  }
}

export function isTourCardDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(TOUR_CARD_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

export function dismissTourCard(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TOUR_CARD_DISMISSED_KEY, "true");
  } catch {
    // ignore
  }
}
